/**
 * Member guests API wrapper (DNKADM-0068 — consumes DNKAPI-0147).
 *
 * A member's **guests** are the people that member has invited onto reservations.
 * Admin (OWNER/ADMIN) sees the full history per member — every status, lifetime +
 * this-month invite counts, first/last invited — and can **moderate** each (member,
 * guest) relationship by `memberGuestId`:
 *  - **ARCHIVE** = soft hide (drops off the member's pick list, but auto-reactivates
 *    if the member invites them again).
 *  - **BLOCK** = hard stop (the member cannot add this guest; only an admin unblock
 *    clears it).
 * Members never moderate — that is admin-only (the customer app only picks from a
 * member's own ACTIVE guests / adds by email). This module is the consumer; it
 * builds no backend.
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the moderation writes.
 *
 * **Endpoints (DNKAPI-0147 hand-off, 2026-06-26):**
 *  - `GET  /v2/admin/members/{contactId}/guests?status=ACTIVE|ARCHIVED|BLOCKED|all`
 *    → `{ guests: MemberGuest[] }` (default `all` — admin sees every status).
 *  - `POST /v2/admin/member-guests/{memberGuestId}/archive`   → ARCHIVED row.
 *  - `POST /v2/admin/member-guests/{memberGuestId}/unarchive` → ACTIVE row.
 *  - `POST /v2/admin/member-guests/{memberGuestId}/block`     → BLOCKED row.
 *  - `POST /v2/admin/member-guests/{memberGuestId}/unblock`   → ACTIVE row.
 *    (Each returns the updated row; idempotent; a foreign-tenant id → `404`.)
 *  - `GET  /v2/admin/guests/{guestUserId}/usage` → cross-member usage totals.
 *
 * Every read is parsed DEFENSIVELY (mirrors `services.ts` post-DNKADM-0064): ids
 * arrive as NUMBERS on the wire and are normalized to the `string` the UI uses via
 * `asId`; counts degrade to 0; an unknown status degrades to `null` (neutral); the
 * guests array is read tolerantly from `{ guests }` OR a bare array. A malformed row
 * (no usable `memberGuestId`) is dropped, never a throw.
 */
import { request } from './client';

/** The moderation lifecycle status of a (member, guest) relationship. */
export type MemberGuestStatus = 'ACTIVE' | 'ARCHIVED' | 'BLOCKED';

/** The `status` query filter for {@link getMemberGuests} — a status or `all`. */
export type MemberGuestStatusFilter = MemberGuestStatus | 'all';

/**
 * One guest the member has invited (DNKAPI-0147). `memberGuestId` keys the
 * relationship (the moderation target); `guestUserId` keys the guest user.
 * Identity fields (`email`/`firstName`/`lastName`) are nullable. `status` is the
 * moderation state, or `null` when the wire value is unknown/absent.
 * `totalInviteCount` is lifetime; `invitesThisMonth` is the current calendar month
 * (both default 0). The timestamps are ISO instants, or `null` when absent.
 */
export interface MemberGuest {
  memberGuestId: string;
  guestUserId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  status: MemberGuestStatus | null;
  totalInviteCount: number;
  invitesThisMonth: number;
  firstInvitedAt: string | null;
  lastInvitedAt: string | null;
}

/**
 * One member's share of a guest's cross-tenant usage ({@link GuestUsage.perMember}).
 * `memberName` is optional (absent when the server carries none); `memberContactId`
 * is the fallback identity. `inviteCount` defaults 0; `status` is that member's
 * moderation state for this guest (or `null`).
 */
export interface GuestUsagePerMember {
  memberContactId: string;
  memberName: string | null;
  inviteCount: number;
  status: MemberGuestStatus | null;
}

/**
 * Cross-member usage for one guest (DNKAPI-0147) — how heavily a guest is invited
 * across the whole tenant. `totalInvitesAllMembers`/`distinctMembers` default 0;
 * `perMember` is the per-member breakdown.
 */
export interface GuestUsage {
  guestUserId: string;
  email: string | null;
  totalInvitesAllMembers: number;
  distinctMembers: number;
  perMember: GuestUsagePerMember[];
}

// --- Defensive parsing helpers (mirrors services.ts) ---------------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 ids arrive as
 * numbers on the wire and are normalized to the `string` the UI uses — a finite
 * number → `String(n)`, a non-empty string → trimmed, else null.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A finite number (number or numeric string), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

/** A finite count ≥ 0 (number or numeric string), or the fallback (default 0). */
function asCount(value: unknown, fallback = 0): number {
  const n = asNumber(value);
  return n !== null && n >= 0 ? n : fallback;
}

/** Parse a raw moderation status; anything unknown/absent degrades to `null`. */
function asStatus(value: unknown): MemberGuestStatus | null {
  if (typeof value === 'string') {
    const upper = value.toUpperCase();
    if (upper === 'ACTIVE') return 'ACTIVE';
    if (upper === 'ARCHIVED') return 'ARCHIVED';
    if (upper === 'BLOCKED') return 'BLOCKED';
  }
  return null;
}

/**
 * Parse one raw guest row, or null when it has no usable `memberGuestId` (a row with
 * no moderation target is dropped rather than surfaced half-identified). Numeric ids
 * normalize to string; identity fields degrade to null; counts default 0; an unknown
 * status → null.
 */
function parseMemberGuest(raw: unknown): MemberGuest | null {
  if (!isObject(raw)) return null;
  const memberGuestId = asId(raw['memberGuestId']);
  if (memberGuestId === null) return null;
  return {
    memberGuestId,
    guestUserId: asId(raw['guestUserId']) ?? '',
    email: asText(raw['email']),
    firstName: asText(raw['firstName']),
    lastName: asText(raw['lastName']),
    status: asStatus(raw['status']),
    totalInviteCount: asCount(raw['totalInviteCount']),
    invitesThisMonth: asCount(raw['invitesThisMonth']),
    firstInvitedAt: asText(raw['firstInvitedAt']),
    lastInvitedAt: asText(raw['lastInvitedAt']),
  };
}

/**
 * Tolerantly extract the guests array from a successful body — a bare array or a
 * `{ guests }` / `{ content }` wrapper — returning `null` when none yields one (the
 * list read turns `null` into `[]`, a real first-run state).
 */
function extractGuests(body: unknown): unknown[] | null {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['guests'])) return body['guests'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return null;
}

/** Parse one raw per-member usage row, or null without a usable `memberContactId`. */
function parsePerMember(raw: unknown): GuestUsagePerMember | null {
  if (!isObject(raw)) return null;
  const memberContactId = asId(raw['memberContactId']);
  if (memberContactId === null) return null;
  return {
    memberContactId,
    memberName: asText(raw['memberName']),
    inviteCount: asCount(raw['inviteCount']),
    status: asStatus(raw['status']),
  };
}

/** Parse a raw usage body defensively (totals default 0; bad perMember rows dropped). */
function parseGuestUsage(raw: unknown, guestUserId: string): GuestUsage {
  const body = isObject(raw) ? raw : {};
  const perMemberRaw = Array.isArray(body['perMember'])
    ? body['perMember']
    : Array.isArray(body['content'])
      ? body['content']
      : [];
  return {
    guestUserId: asId(body['guestUserId']) ?? guestUserId,
    email: asText(body['email']),
    totalInvitesAllMembers: asCount(body['totalInvitesAllMembers']),
    distinctMembers: asCount(body['distinctMembers']),
    perMember: perMemberRaw
      .map(parsePerMember)
      .filter((p): p is GuestUsagePerMember => p !== null),
  };
}

// --- Path helpers --------------------------------------------------------------

/** A member's guests collection (`/v2/admin/members/{contactId}/guests`). */
function memberGuestsPath(contactId: string): string {
  return `/v2/admin/members/${encodeURIComponent(contactId)}/guests`;
}

/** A moderation verb on one relationship (`/v2/admin/member-guests/{id}/{verb}`). */
function moderationPath(memberGuestId: string, verb: string): string {
  return `/v2/admin/member-guests/${encodeURIComponent(memberGuestId)}/${verb}`;
}

// --- Reads (U1) ----------------------------------------------------------------

/**
 * `GET /v2/admin/members/{contactId}/guests?status=…` → the member's guests,
 * defensively parsed (a bad row dropped). `status` defaults to `all` (admin sees
 * every status; the member's own pick list hides archived/blocked, customer-side).
 * Throws a typed `ApiError` on a non-2xx response.
 */
export async function getMemberGuests(
  contactId: string,
  status: MemberGuestStatusFilter = 'all',
): Promise<MemberGuest[]> {
  const body = await request<unknown>('GET', memberGuestsPath(contactId), {
    query: { status },
  });
  return (extractGuests(body) ?? [])
    .map(parseMemberGuest)
    .filter((g): g is MemberGuest => g !== null);
}

/**
 * `GET /v2/admin/guests/{guestUserId}/usage` → the guest's cross-member usage
 * (totals + the per-member breakdown), defensively parsed. (U3 consumes.) Throws a
 * typed `ApiError` on a non-2xx response.
 */
export async function getGuestUsage(guestUserId: string): Promise<GuestUsage> {
  const body = await request<unknown>(
    'GET',
    `/v2/admin/guests/${encodeURIComponent(guestUserId)}/usage`,
  );
  return parseGuestUsage(body, guestUserId);
}

// --- Moderation (consumed by U2) -----------------------------------------------

/**
 * `POST /v2/admin/member-guests/{id}/{verb}` → run a moderation verb and re-parse
 * the updated row. A `404` (a foreign-tenant / stale id) surfaces as the typed
 * `ApiError` (`NOT_FOUND`) for the caller to map to "guest record not found —
 * refresh". The updated row is `null` only if the response body is malformed (the
 * caller refetches regardless). Idempotent server-side.
 */
async function moderate(memberGuestId: string, verb: string): Promise<MemberGuest | null> {
  const body = await request<unknown>('POST', moderationPath(memberGuestId, verb));
  return parseMemberGuest(body);
}

/** `POST …/{id}/archive` → soft-hide the guest (ARCHIVED). (U2 consumes.) */
export async function archiveMemberGuest(memberGuestId: string): Promise<MemberGuest | null> {
  return moderate(memberGuestId, 'archive');
}

/** `POST …/{id}/unarchive` → restore a soft-hidden guest (ACTIVE). (U2 consumes.) */
export async function unarchiveMemberGuest(memberGuestId: string): Promise<MemberGuest | null> {
  return moderate(memberGuestId, 'unarchive');
}

/** `POST …/{id}/block` → hard-stop the guest (BLOCKED). (U2 consumes.) */
export async function blockMemberGuest(memberGuestId: string): Promise<MemberGuest | null> {
  return moderate(memberGuestId, 'block');
}

/** `POST …/{id}/unblock` → lift a block (ACTIVE) — admin-only. (U2 consumes.) */
export async function unblockMemberGuest(memberGuestId: string): Promise<MemberGuest | null> {
  return moderate(memberGuestId, 'unblock');
}
